import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import Layout from '../../components/ui/Layout';
import { Container, Card, Button, Form, Row, Col, Table, Spinner, Alert, Badge } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../components/context/AuthContext';
import { calculateDealPrice } from '../../utils/pricing/dealPricing';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { createLogger } from '../../utils/logger';

const logger = createLogger('ReportPage');

export default function ReportPage() {
  const { userRole, employeeSupermarketId } = useAuth();
  const [supermarketFilter, setSupermarketFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');
  const [showSummaryOnly, setShowSummaryOnly] = useState(false);
  const [supermarketOptions, setSupermarketOptions] = useState([]);
  const [inventoryDetails, setInventoryDetails] = useState({}); // { [productId_supermarketId]: inventoryData }
  const location = useLocation();

  // Parse supermarket filter from query string
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const supermarketParam = params.get('supermarket');
    if (supermarketParam) {
      setSupermarketFilter(supermarketParam);
    }
  }, [location.search]);

  // Only allow employees to see their assigned supermarket's reports
  useEffect(() => {
    if (userRole === 'Employee' && employeeSupermarketId) {
      setSupermarketFilter(employeeSupermarketId); // This should match the supermarketName in your data
    }
  }, [userRole, employeeSupermarketId]);

  // Set default filter for employees ONCE when employeeSupermarketId is available
  useEffect(() => {
    if (
      userRole === 'Employee' &&
      employeeSupermarketId &&
      supermarketFilter !== employeeSupermarketId
    ) {
      setSupermarketFilter(employeeSupermarketId);
      
    }
  }, [userRole, employeeSupermarketId, supermarketFilter]);

  // Function to calculate deal price for a product
  const calculateDealPriceForProduct = (productId, quantity, regularPrice, supermarketId) => {
    const inventoryKey = `${productId}_${supermarketId}`;
    return calculateDealPrice({
      quantity,
      regularPrice,
      inventoryData: inventoryDetails[inventoryKey],
      includeOriginalPrice: true,
    });
  };

  // Function to fetch inventory details for deals
  const fetchInventoryDetails = async (productId, supermarketId) => {
    const key = `${productId}_${supermarketId}`;
    if (inventoryDetails[key]) return; // Already fetched
    
    try {
      const inventoryRef = collection(db, 'Inventory');
      const inventoryQuery = query(
        inventoryRef,
        where('product_id', '==', productId),
        where('supermarket_id', '==', supermarketId),
        limit(1)
      );
      
      const inventorySnapshot = await getDocs(inventoryQuery);
      if (!inventorySnapshot.empty) {
        const inventoryData = inventorySnapshot.docs[0].data();
        setInventoryDetails(prev => ({ ...prev, [key]: inventoryData }));
      }
    } catch (error) {
      logger.error(`Failed to fetch inventory details for ${productId}:`, error);
    }
  };

  // Fetch all supermarkets for dropdown (include full_payment_enabled for employee visibility)
  useEffect(() => {
    async function fetchSupermarkets() {
      const snapshot = await getDocs(collection(db, 'Supermarkets'));
      const options = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        options.push({
          id: doc.id,
          display_name: data.display_name,
          full_payment_enabled: !!data.full_payment_enabled
        });
      });
      setSupermarketOptions(options);
    }
    fetchSupermarkets();
  }, []);

  const handleFetch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setOrders([]);
    try {
      const start = startDate ? new Date(startDate) : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const end = endDate ? new Date(endDate) : new Date();
      
      // Adjust end date to include the entire day
      end.setHours(23, 59, 59, 999);
      
      logger.debug('[ReportPage] Fetching orders for date range:', { start, end, supermarketFilter });
      
      // First, try to get orders from Transactions collection (more reliable)
      const transactionsSnapshot = await getDocs(collection(db, 'Transactions'));
      const transactionOrders = [];
      
      transactionsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.createdAt) {
          const createdAtDate = data.createdAt.toDate();
          if (createdAtDate >= start && createdAtDate <= end) {
            // Check if this transaction matches our supermarket filter
            if (!supermarketFilter || data.supermarketId === supermarketFilter) {
              transactionOrders.push({
                ...data,
                transactionId: doc.id,
                instanceId: data.cartInstanceId || doc.id,
                supermarketId: data.supermarketId,
                source: 'transactions'
              });
            }
          }
        }
      });
      
      logger.debug('[ReportPage] Found transactions:', transactionOrders.length);
      
      // Also check Carts collection as fallback
      const snapshot = await getDocs(collection(db, 'Carts'));
      const cartOrders = [];
      
      snapshot.forEach(doc => {
        const data = doc.data();
        for (const supermarketId in data) {
          if (supermarketId === 'lastGlobalUpdate') continue;
          const supermarketData = data[supermarketId];
          for (const instanceId in supermarketData) {
            const instance = supermarketData[instanceId];
            if (instance.createdAt) {
              const createdAtDate = instance.createdAt.toDate();
              if (createdAtDate >= start && createdAtDate <= end) {
                // Check if this cart matches our supermarket filter
                const matchesFilter = !supermarketFilter || 
                  instance.supermarketId === supermarketFilter ||
                  supermarketId === supermarketFilter;
                
                if (matchesFilter) {
                  cartOrders.push({
                    ...instance,
                    cartId: doc.id,
                    instanceId: instanceId || `${doc.id}_${supermarketId}`,
                    supermarketId: instance.supermarketId || supermarketId,
                    source: 'carts'
                  });
                }
              }
            }
          }
        }
      });
      
      logger.debug('[ReportPage] Found cart orders:', cartOrders.length);
      
      // Combine and deduplicate orders (prefer transactions over carts)
      const allOrders = [...transactionOrders];
      const existingIds = new Set(transactionOrders.map(o => o.cartInstanceId || o.instanceId));
      
      cartOrders.forEach(cartOrder => {
        const cartOrderId = cartOrder.instanceId || cartOrder.cartInstanceId;
        if (!existingIds.has(cartOrderId)) {
          allOrders.push(cartOrder);
        }
      });
      
      // Additional deduplication by cartInstanceId to ensure uniqueness
      const uniqueOrders = [];
      const seenIds = new Set();
      
      allOrders.forEach(order => {
        const orderId = order.cartInstanceId || order.instanceId || order.transactionId;
        if (!seenIds.has(orderId)) {
          seenIds.add(orderId);
          uniqueOrders.push(order);
        }
      });
      
      logger.debug('[ReportPage] Total unique orders:', uniqueOrders.length);
      setOrders(uniqueOrders);

      // Fetch inventory details for all products in the orders
      const productSupermarketPairs = new Set();
      uniqueOrders.forEach(order => {
        if (order.cartItems && Array.isArray(order.cartItems)) {
          order.cartItems.forEach(product => {
            if (product.productId && order.supermarketId) {
              productSupermarketPairs.add(`${product.productId}_${order.supermarketId}`);
            }
          });
        } else if (order.products && Array.isArray(order.products)) {
          // Fallback for cart data structure
          order.products.forEach(product => {
            if (product.productId && order.supermarketId) {
              productSupermarketPairs.add(`${product.productId}_${order.supermarketId}`);
            }
          });
        }
      });

      // Fetch inventory details for all product-supermarket pairs
      const fetchPromises = Array.from(productSupermarketPairs).map(pair => {
        const [productId, supermarketId] = pair.split('_');
        return fetchInventoryDetails(productId, supermarketId);
      });

      await Promise.all(fetchPromises);

    } catch (err) {
      setError(`Failed to fetch report: ${  err.message}`);
      logger.error('Error fetching report:', err);
    }
    setLoading(false);
  };

  // Filter orders for table rendering
  const selectedSupermarket = supermarketOptions.find(opt => opt.id === supermarketFilter);
  const selectedDisplayName = selectedSupermarket ? selectedSupermarket.display_name : '';

  const filteredOrders = orders.filter(order => {
    // If no filter is set, show all orders
    if (!supermarketFilter) return true;
    
    // Check if order matches the selected supermarket by ID or name
    return order.supermarketId === supermarketFilter ||
           order.supermarketName === selectedDisplayName ||
           order.supermarketName === selectedSupermarket?.display_name;
  });

  // Calculate confirmation fee (needed before isFullPaymentOrder function)
  const confirmationFee = Number(process.env.REACT_APP_CONFIRMATION_FEE || 0);

  const getOrderDeliveryFee = (order) => {
    if (order.deliveryOption !== 'delivery') return 0;
    return Number(order.deliveryFee) || 0;
  };

  // Helper function to determine if an order is a full payment
  const isFullPaymentOrder = (order) => {
    // Check paymentType field first
    if (order.paymentType === 'full') {
      return true;
    }
    
    // Fallback: Check if amount paid equals order subtotal + confirmation fee + delivery fee
    const orderSubtotal = Number(order.orderSubtotal) || 0;
    const confirmationFeeAmount = Number(order.confirmationFee) || confirmationFee;
    const deliveryFeeAmount = getOrderDeliveryFee(order);
    const expectedFullTotal = orderSubtotal + confirmationFeeAmount + deliveryFeeAmount;
    const amountPaid = parseFloat(order.amountPaid) || parseFloat(order.total) || 0;
    
    // If amount paid is close to or equals the full total (within 0.01 for rounding), it's a full payment
    if (orderSubtotal > 0 && Math.abs(amountPaid - expectedFullTotal) < 0.01) {
      return true;
    }
    
    return false;
  };

  // Group and summarize orders by supermarket and status
  const summary = {};
  filteredOrders.forEach(order => {

    const supermarket = order.supermarketName || 'N/A';
    const status = order.status || 'N/A';
    const key = `${supermarket}|||${status}`;
    
    let total = 0;
    let originalTotal = 0;
    let totalSavings = 0;
    let dealsApplied = 0;

    // Handle both cartItems (from transactions) and products (from carts) arrays
    const items = order.cartItems || order.products || [];
    if (Array.isArray(items)) {
      items.forEach(product => {
        const dealCalculation = calculateDealPriceForProduct(
          product.productId, 
          product.quantity || 1, 
          product.price || 0, 
          order.supermarketId
        );
        total += dealCalculation.price;
        originalTotal += dealCalculation.originalPrice;
        totalSavings += dealCalculation.savings;
        if (dealCalculation.dealApplied) {
          dealsApplied++;
        }
      });
    }

    const isFullPayment = isFullPaymentOrder(order);
    const orderDeliveryFee = getOrderDeliveryFee(order);
    
    if (!summary[key]) {
      summary[key] = { 
        supermarket, 
        status, 
        total: 0, 
        originalTotal: 0,
        totalSavings: 0,
        dealsApplied: 0,
        deliveryFees: 0,
        count: 0,
        fullPaymentCount: 0,
        confirmationFeeOnlyCount: 0
      };
    }
    summary[key].total += total;
    summary[key].originalTotal += originalTotal;
    summary[key].totalSavings += totalSavings;
    summary[key].dealsApplied += dealsApplied;
    summary[key].deliveryFees += orderDeliveryFee;
    summary[key].count += 1;
    if (isFullPayment) {
      summary[key].fullPaymentCount = (summary[key].fullPaymentCount || 0) + 1;
    } else {
      summary[key].confirmationFeeOnlyCount = (summary[key].confirmationFeeOnlyCount || 0) + 1;
    }
  });

  // Calculate column totals
  const totalSum = Object.values(summary).reduce((acc, s) => acc + s.total, 0);
  const totalOriginalSum = Object.values(summary).reduce((acc, s) => acc + s.originalTotal, 0);
  const totalSavingsSum = Object.values(summary).reduce((acc, s) => acc + s.totalSavings, 0);
  const totalDealsApplied = Object.values(summary).reduce((acc, s) => acc + s.dealsApplied, 0);
  const totalCount = Object.values(summary).reduce((acc, s) => acc + s.count, 0);

  // Calculate confirmation fee total (excluding cancelled_by_employee and active)
  const confirmationFeeTotal = filteredOrders.filter(
    order => order.status !== 'cancelled_by_employee' && order.status !== 'active'
  ).length * confirmationFee;

  // Calculate full payment total (sum of orderSubtotal + confirmationFee for full payment orders)
  const fullPaymentTotal = filteredOrders
    .filter(order => {
      const isActive = order.status !== 'cancelled_by_employee' && order.status !== 'active';
      return isActive && isFullPaymentOrder(order);
    })
    .reduce((total, order) => {
      const orderSubtotal = Number(order.orderSubtotal) || 0;
      const confirmationFeeAmount = Number(order.confirmationFee) || confirmationFee;
      const deliveryFeeAmount = getOrderDeliveryFee(order);
      return total + orderSubtotal + confirmationFeeAmount + deliveryFeeAmount;
    }, 0);

  // Calculate total delivery fees paid at checkout
  const deliveryFeesTotal = filteredOrders
    .filter(order => order.status !== 'cancelled_by_employee' && order.status !== 'active')
    .reduce((total, order) => total + getOrderDeliveryFee(order), 0);

  // Calculate full payment minus confirmation fee (order subtotal portion of full payments)
  const fullPaymentMinusConfirmationFee = filteredOrders
    .filter(order => {
      const isActive = order.status !== 'cancelled_by_employee' && order.status !== 'active';
      return isActive && isFullPaymentOrder(order);
    })
    .reduce((total, order) => {
      const orderSubtotal = Number(order.orderSubtotal) || 0;
      return total + orderSubtotal;
    }, 0);

  // Calculate full payment minus confirmation fee for completed orders only
  const fullPaymentMinusConfirmationFeeCompleted = filteredOrders
    .filter(order => {
      return order.status === 'completed' && isFullPaymentOrder(order);
    })
    .reduce((total, order) => {
      const orderSubtotal = Number(order.orderSubtotal) || 0;
      return total + orderSubtotal;
    }, 0);

  // Employees see "Full Payment minus Confirmation Fee (Completed Only)" only if their supermarket offers full payment
  const employeeSupermarketOffersFullPayment = !!supermarketOptions.find(
    o => o.id === employeeSupermarketId
  )?.full_payment_enabled;
  const showCompletedOnlyRow =
    userRole === 'Admin' || (userRole === 'Employee' && employeeSupermarketOffersFullPayment);

  // PDF generation function - uses component-level filteredOrders and financial totals
  const generatePDF = () => {
    const doc = new jsPDF();
    doc.text('Order Report', 14, 16);

    // Table columns and rows
    const tableColumn = showSummaryOnly
      ? ['Supermarket', 'Status', 'Payment Type', 'Original Total', 'Final Total', 'Savings', 'Deals Applied', 'Delivery Fee', 'Count']
      : ['Supermarket', 'Status', 'Payment Type', 'Original Total', 'Final Total', 'Savings', 'Deals Applied', 'Delivery Fee'];

    const tableRows = showSummaryOnly
      ? Object.values(summary).map(({ supermarket, status, total, originalTotal, totalSavings, dealsApplied, count, fullPaymentCount = 0, confirmationFeeOnlyCount = 0, deliveryFees = 0 }) => {
          const paymentTypeText = fullPaymentCount > 0 && confirmationFeeOnlyCount > 0
            ? `${fullPaymentCount} Full, ${confirmationFeeOnlyCount} Fee Only`
            : fullPaymentCount > 0
            ? `All Full Payment (${fullPaymentCount})`
            : `Confirmation Fee Only (${confirmationFeeOnlyCount})`;
          
          return [
            supermarket, 
            status,
            paymentTypeText,
            `$${originalTotal.toFixed(2)}`, 
            `$${total.toFixed(2)}`, 
            totalSavings > 0 ? `-$${totalSavings.toFixed(2)}` : '$0.00', 
            dealsApplied.toString(),
            deliveryFees > 0 ? `$${deliveryFees.toFixed(2)}` : '$0.00',
            count.toString()
          ];
        })
      : filteredOrders.map(order => {
          let total = 0;
          let originalTotal = 0;
          let totalSavings = 0;
          let dealsApplied = 0;

          // Handle both cartItems (from transactions) and products (from carts) arrays
          const items = order.cartItems || order.products || [];
          if (Array.isArray(items)) {
            items.forEach(product => {
              const dealCalculation = calculateDealPriceForProduct(
                product.productId, 
                product.quantity || 1, 
                product.price || 0, 
                order.supermarketId
              );
              total += dealCalculation.price;
              originalTotal += dealCalculation.originalPrice;
              totalSavings += dealCalculation.savings;
              if (dealCalculation.dealApplied) {
                dealsApplied++;
              }
            });
          }

          const isFullPayment = isFullPaymentOrder(order);

          return [
            order.supermarketName || 'N/A',
            order.status || 'N/A',
            isFullPayment ? 'Full Payment' : 'Confirmation Fee Only',
            `$${originalTotal.toFixed(2)}`,
            `$${total.toFixed(2)}`,
            totalSavings > 0 ? `-$${totalSavings.toFixed(2)}` : '$0.00',
            dealsApplied.toString(),
            getOrderDeliveryFee(order) > 0 ? `$${getOrderDeliveryFee(order).toFixed(2)}` : '$0.00'
          ];
        });

    // Add details or summary table
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 22,
      styles: { 
        cellPadding: 3,
        fontSize: 9
      },
      columnStyles: {
        0: { halign: 'left', cellWidth: 'auto' },  // Supermarket
        1: { halign: 'left', cellWidth: 'auto' },  // Status
        2: { halign: 'left', cellWidth: 'auto' },  // Payment Type
        3: { halign: 'right', cellWidth: 'auto' }, // Original Total
        4: { halign: 'right', cellWidth: 'auto' }, // Final Total
        5: { halign: 'right', cellWidth: 'auto' }, // Savings
        6: { halign: 'right', cellWidth: 'auto' }, // Deals Applied
        7: { halign: 'right', cellWidth: 'auto' }, // Delivery Fee
        ...(showSummaryOnly && { 8: { halign: 'right', cellWidth: 'auto' } }) // Count
      },
      headStyles: { 
        fillColor: [66, 139, 202],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'left'
      },
      headStylesOverrides: {
        fontSize: 10
      },
      margin: { left: 14, right: 14 }
    });

    // Add summary section (always at the end)
    const summaryStartY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 32;
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Summary', 14, summaryStartY);

    // Create totals row that matches the table structure
    const totalsRow = showSummaryOnly
      ? ['Totals', '', '', `$${totalOriginalSum.toFixed(2)}`, `$${totalSum.toFixed(2)}`, `-$${totalSavingsSum.toFixed(2)}`, totalDealsApplied.toString(), `$${deliveryFeesTotal.toFixed(2)}`, totalCount.toString()]
      : ['Totals', '', '', `$${totalOriginalSum.toFixed(2)}`, `$${totalSum.toFixed(2)}`, `-$${totalSavingsSum.toFixed(2)}`, totalDealsApplied.toString(), `$${deliveryFeesTotal.toFixed(2)}`];

    // Totals row for summary table - use autoTable for proper alignment
    autoTable(doc, {
      head: [totalsRow],
      body: [],
      startY: summaryStartY + 6,
      theme: 'plain',
      styles: { 
        fontStyle: 'bold',
        halign: 'left',
        cellPadding: 3
      },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'left' },
        2: { halign: 'left' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' },
        7: { halign: 'right' },
        ...(showSummaryOnly && { 8: { halign: 'right' } })
      },
      headStyles: { 
        fillColor: [248, 249, 250],
        textColor: [0, 0, 0],
        fontStyle: 'bold'
      },
      margin: { left: 14, right: 14 }
    });

    // Financial summary: Admin sees all four totals; Employee sees "Completed Only" only if their supermarket offers full payment
    if (userRole === 'Admin' || (userRole === 'Employee' && showCompletedOnlyRow)) {
      const financialTotalsY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : summaryStartY + 20;
      
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      
      const formatCurrency = (value) => {
        const numValue = Number(value) || 0;
        return `$${numValue.toFixed(2)}`;
      };
      
      const fullPaymentMinusFeeCompletedStr = formatCurrency(fullPaymentMinusConfirmationFeeCompleted);
      const bodyRows = [
        ['Delivery Fees Paid:', formatCurrency(deliveryFeesTotal)],
        ['Full Payment minus Confirmation Fee (Completed Only):', fullPaymentMinusFeeCompletedStr]
      ];
      if (userRole === 'Admin') {
        bodyRows.unshift(
          ['Confirmation Fee Total:', formatCurrency(confirmationFeeTotal)],
          ['Full Payment Total:', formatCurrency(fullPaymentTotal)],
          ['Full Payment minus Confirmation Fee:', formatCurrency(fullPaymentMinusConfirmationFee)]
        );
      }

      logger.debug('[ReportPage] PDF Financial Totals:', { userRole, bodyRows });
      
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 14 * 2;
      const availableWidth = pageWidth - margin;
      const labelColumnWidth = availableWidth * 0.65;
      const amountColumnWidth = availableWidth * 0.35;
      
      try {
        autoTable(doc, {
          head: [['Financial Summary', 'Amount']],
          body: bodyRows,
          startY: financialTotalsY,
          theme: 'grid',
          styles: { 
            fontStyle: 'bold',
            cellPadding: 3,
            fontSize: 9,
            overflow: 'linebreak'
          },
          columnStyles: {
            0: { halign: 'left', cellWidth: labelColumnWidth },
            1: { halign: 'right', cellWidth: amountColumnWidth }
          },
          headStyles: { 
            fillColor: [66, 139, 202],
            textColor: [255, 255, 255],
            fontStyle: 'bold'
          },
          margin: { left: 14, right: 14 },
          tableWidth: 'wrap'
        });
      } catch (error) {
        logger.error('[ReportPage] Error rendering financial totals table:', error);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        let yPos = financialTotalsY;
        doc.text('Financial Summary', 14, yPos);
        yPos += 6;
        doc.setFont(undefined, 'normal');
        if (userRole === 'Admin') {
          doc.text(`Confirmation Fee Total: ${formatCurrency(confirmationFeeTotal)}`, 14, yPos);
          yPos += 6;
          doc.text(`Full Payment Total: ${formatCurrency(fullPaymentTotal)}`, 14, yPos);
          yPos += 6;
          doc.text(`Full Payment minus Confirmation Fee: ${formatCurrency(fullPaymentMinusConfirmationFee)}`, 14, yPos);
          yPos += 6;
        }
        doc.text(`Full Payment minus Confirmation Fee (Completed Only): ${fullPaymentMinusFeeCompletedStr}`, 14, yPos);
        yPos += 6;
        doc.text(`Delivery Fees Paid: ${formatCurrency(deliveryFeesTotal)}`, 14, yPos);
      }
    }

    doc.save('order_report.pdf');
  };

  return (
    <Layout>
      <Container className="mt-4 px-3 mb-5">
        <Card className="shadow-sm">
          <Card.Body className="p-3 p-md-4">
            <Card.Title>Order Report</Card.Title>
            <Form onSubmit={handleFetch} className="mb-3">
              <Row className="g-3">
                <Col xs={12} sm={6} md={5}>
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      required
                      className="w-100"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6} md={5}>
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      required
                      className="w-100"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={12} md={2} className="d-flex align-items-end">
                  <Button 
                    type="submit" 
                    variant="primary" 
                    disabled={loading}
                    className="w-100 w-sm-auto"
                  >
                    {loading ? <Spinner size="sm" animation="border" /> : 'Generate'}
                  </Button>
                </Col>
              </Row>
            </Form>
            <Form.Group className="mb-3" controlId="supermarketFilter">
              <Form.Label>Filter by Supermarket</Form.Label>
              <Form.Select
                value={supermarketFilter}
                onChange={e => setSupermarketFilter(e.target.value)}
                className="w-100"
                style={{ maxWidth: '100%' }}
                disabled={userRole === 'Employee'}
              >
                <option value="">All Supermarkets</option>
                {supermarketOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.display_name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Check
              type="switch"
              id="show-summary-only-switch"
              label="Show Summary Only"
              checked={showSummaryOnly}
              onChange={() => setShowSummaryOnly(v => !v)}
              style={{ marginBottom: '12px' }}
            />
            {error && <Alert variant="danger">{error}</Alert>}
            {loading ? (
              <div>Loading...</div>
            ) : filteredOrders.length > 0 ? (
              showSummaryOnly ? (
                // Show only the summary table
                <div className="table-responsive">
                  <Table striped bordered hover className="table-sm">
                  <thead>
                    <tr>
                      <th>Supermarket</th>
                      <th>Status</th>
                      <th>Payment Type</th>
                      <th>Original Total</th>
                      <th>Final Total</th>
                      <th>Savings</th>
                      <th>Deals Applied</th>
                      <th>Delivery Fee</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(summary).map(({ supermarket, status, total, originalTotal, totalSavings, dealsApplied, deliveryFees = 0, count, fullPaymentCount = 0, confirmationFeeOnlyCount = 0 }) => (
                      <tr key={supermarket + status}>
                        <td>{supermarket}</td>
                        <td>{status}</td>
                        <td>
                          {fullPaymentCount > 0 && confirmationFeeOnlyCount > 0 ? (
                            <div>
                              <Badge bg="success" className="me-1">{fullPaymentCount} Full</Badge>
                              <Badge bg="secondary">{confirmationFeeOnlyCount} Fee Only</Badge>
                            </div>
                          ) : fullPaymentCount > 0 ? (
                            <Badge bg="success">All Full Payment ({fullPaymentCount})</Badge>
                          ) : (
                            <Badge bg="secondary">Confirmation Fee Only ({confirmationFeeOnlyCount})</Badge>
                          )}
                        </td>
                        <td>${originalTotal.toFixed(2)}</td>
                        <td>${total.toFixed(2)}</td>
                        <td>
                          {totalSavings > 0 ? (
                            <span className="text-success fw-bold">-${totalSavings.toFixed(2)}</span>
                          ) : (
                            <span className="text-muted">$0.00</span>
                          )}
                        </td>
                        <td>
                          {dealsApplied > 0 ? (
                            <Badge bg="success">{dealsApplied}</Badge>
                          ) : (
                            <span className="text-muted">0</span>
                          )}
                        </td>
                        <td>
                          {deliveryFees > 0 ? (
                            <span className="fw-bold">${deliveryFees.toFixed(2)}</span>
                          ) : (
                            <span className="text-muted">$0.00</span>
                          )}
                        </td>
                        <td>{count}</td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 'bold', background: '#f8f9fa' }}>
                      <td colSpan={3}>Totals</td>
                      <td>${totalOriginalSum.toFixed(2)}</td>
                      <td>${totalSum.toFixed(2)}</td>
                      <td className="text-success fw-bold">-${totalSavingsSum.toFixed(2)}</td>
                      <td>{totalDealsApplied}</td>
                      <td>${deliveryFeesTotal.toFixed(2)}</td>
                      <td>{totalCount}</td>
                    </tr>
                  </tbody>
                </Table>
                </div>
              ) : (
                // Show only the details table
                <div className="table-responsive">
                  <Table striped bordered hover className="table-sm">
                  <thead>
                    <tr>
                      <th>Supermarket</th>
                      <th>Status</th>
                      <th>Payment Type</th>
                      <th>Original Total</th>
                      <th>Final Total</th>
                      <th>Savings</th>
                      <th>Deals Applied</th>
                      <th>Delivery Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map(order => {
                      let total = 0;
                      let originalTotal = 0;
                      let totalSavings = 0;
                      let dealsApplied = 0;

                      // Handle both cartItems (from transactions) and products (from carts) arrays
                      const items = order.cartItems || order.products || [];
                      if (Array.isArray(items)) {
                        items.forEach(product => {
                          const dealCalculation = calculateDealPriceForProduct(
                            product.productId, 
                            product.quantity || 1, 
                            product.price || 0, 
                            order.supermarketId
                          );
                          total += dealCalculation.price;
                          originalTotal += dealCalculation.originalPrice;
                          totalSavings += dealCalculation.savings;
                          if (dealCalculation.dealApplied) {
                            dealsApplied++;
                          }
                        });
                      }

                      const isFullPayment = isFullPaymentOrder(order);

                      return (
                        <tr key={`${order.source || 'unknown'}-${order.instanceId || order.transactionId || order.cartId}-${order.supermarketId || 'unknown'}`}>
                          <td>{order.supermarketName || 'N/A'}</td>
                          <td>{order.status || 'N/A'}</td>
                          <td>
                            {isFullPayment ? (
                              <Badge bg="success">
                                <i className="fas fa-check-circle me-1"></i>
                                Full Payment
                              </Badge>
                            ) : (
                              <Badge bg="secondary">Confirmation Fee Only</Badge>
                            )}
                          </td>
                          <td>${originalTotal.toFixed(2)}</td>
                          <td>${total.toFixed(2)}</td>
                          <td>
                            {totalSavings > 0 ? (
                              <span className="text-success fw-bold">-${totalSavings.toFixed(2)}</span>
                            ) : (
                              <span className="text-muted">$0.00</span>
                            )}
                          </td>
                          <td>
                            {dealsApplied > 0 ? (
                              <Badge bg="success">{dealsApplied}</Badge>
                            ) : (
                              <span className="text-muted">0</span>
                            )}
                          </td>
                          <td>
                            {getOrderDeliveryFee(order) > 0 ? (
                              <span className="fw-bold">${getOrderDeliveryFee(order).toFixed(2)}</span>
                            ) : (
                              <span className="text-muted">$0.00</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                </div>
              )
            ) : (
              <div>No orders found for this range.</div>
            )}
            {!showSummaryOnly && filteredOrders.length > 0 && (
              <div className="table-responsive">
                <Table striped bordered hover className="table-sm">
                <thead>
                  <tr>
                    <th>Supermarket</th>
                    <th>Status</th>
                    <th>Payment Type</th>
                    <th>Original Total</th>
                    <th>Final Total</th>
                    <th>Savings</th>
                    <th>Deals Applied</th>
                    <th>Delivery Fee</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(summary).map(({ supermarket, status, total, originalTotal, totalSavings, dealsApplied, deliveryFees = 0, count, fullPaymentCount = 0, confirmationFeeOnlyCount = 0 }) => (
                    <tr key={supermarket + status}>
                      <td>{supermarket}</td>
                      <td>{status}</td>
                      <td>
                        {fullPaymentCount > 0 && confirmationFeeOnlyCount > 0 ? (
                          <div>
                            <Badge bg="success" className="me-1">{fullPaymentCount} Full</Badge>
                            <Badge bg="secondary">{confirmationFeeOnlyCount} Fee Only</Badge>
                          </div>
                        ) : fullPaymentCount > 0 ? (
                          <Badge bg="success">All Full Payment ({fullPaymentCount})</Badge>
                        ) : (
                          <Badge bg="secondary">Confirmation Fee Only ({confirmationFeeOnlyCount})</Badge>
                        )}
                      </td>
                      <td>${originalTotal.toFixed(2)}</td>
                      <td>${total.toFixed(2)}</td>
                      <td>
                        {totalSavings > 0 ? (
                          <span className="text-success fw-bold">-${totalSavings.toFixed(2)}</span>
                        ) : (
                          <span className="text-muted">$0.00</span>
                        )}
                      </td>
                      <td>
                        {dealsApplied > 0 ? (
                          <Badge bg="success">{dealsApplied}</Badge>
                        ) : (
                          <span className="text-muted">0</span>
                        )}
                      </td>
                      <td>
                        {deliveryFees > 0 ? (
                          <span className="fw-bold">${deliveryFees.toFixed(2)}</span>
                        ) : (
                          <span className="text-muted">$0.00</span>
                        )}
                      </td>
                      <td>{count}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 'bold', background: '#f8f9fa' }}>
                    <td colSpan={3}>Totals</td>
                    <td>${totalOriginalSum.toFixed(2)}</td>
                    <td>${totalSum.toFixed(2)}</td>
                    <td className="text-success fw-bold">-${totalSavingsSum.toFixed(2)}</td>
                    <td>{totalDealsApplied}</td>
                    <td>${deliveryFeesTotal.toFixed(2)}</td>
                    <td>{totalCount}</td>
                  </tr>
                </tbody>
                </Table>
                </div>
            )}
            {(userRole === 'Admin' || userRole === 'Employee') && (
              <div style={{ fontWeight: 'bold', marginTop: '1rem' }}>
                <div>Delivery Fees Paid: ${(Number(deliveryFeesTotal) || 0).toFixed(2)}</div>
                {userRole === 'Admin' && (
                  <>
                    <div>Confirmation Fee Total: ${(Number(confirmationFeeTotal) || 0).toFixed(2)}</div>
                    <div>Full Payment Total: ${(Number(fullPaymentTotal) || 0).toFixed(2)}</div>
                    <div>Full Payment minus Confirmation Fee: ${(Number(fullPaymentMinusConfirmationFee) || 0).toFixed(2)}</div>
                  </>
                )}
                {showCompletedOnlyRow && (
                  <div>Full Payment minus Confirmation Fee (Completed Only): ${(Number(fullPaymentMinusConfirmationFeeCompleted) || 0).toFixed(2)}</div>
                )}
              </div>
            )}
            <Button
              variant="secondary"
              style={{ marginTop: '1rem' }}
              onClick={generatePDF}
              disabled={
                (showSummaryOnly && Object.values(summary).length === 0) ||
                (!showSummaryOnly && filteredOrders.length === 0)
              }
            >
              Download PDF
            </Button>
          </Card.Body>
        </Card>
      </Container>
    </Layout>
  );
}